import { AuthSubscriptionResponse, SubscriptionPlan } from '../types/subscription';
import { BaseService } from './base';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  current_usage: number;
  approval_required: boolean;
  settings?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export interface User {
  id: string;
  email: string;
  approval_required: boolean;
  current_usage: number;
  created_at: string;
  current_organization_id?: string;
  organization_role?: string;
}

// Use standardized type for subscription responses
export type Subscription = AuthSubscriptionResponse;

export interface AuthResponse {
  message: string;
  user: User;
  organization?: Organization;
  session?: {
    access_token: string;
    refresh_token: string;
    expires_at: number;
  };
  subscription?: Subscription;
}

export interface ApiError {
  error: string;
  message: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

class AuthService extends BaseService {

  async register(email: string, password: string, confirmPassword: string): Promise<AuthResponse> {
    const response = await this.makeRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, confirmPassword }),
    });

    if (response.session) {
      this.setTokens(response.session.access_token, response.session.refresh_token);
    }

    return response;
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.makeRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.session) {
      this.setTokens(response.session.access_token, response.session.refresh_token);
    }

    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.makeRequest('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Continue with local logout even if API call fails
      console.error('Logout API call failed:', error);
    }

    this.clearTokens();
  }

  async getCurrentUser(): Promise<AuthResponse> {
    const token = this.getAccessToken();
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    return this.makeRequest<AuthResponse>('/auth/user');
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      const error = new Error('No refresh token found');
      throw error;
    }

    const response = await this.makeRequest<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.session) {
      this.setTokens(response.session.access_token, response.session.refresh_token);
    }

    return response;
  }

  /**
   * Request a password reset email
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Update password using the recovery token from email
   */
  async updatePassword(accessToken: string, newPassword: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/auth/update-password', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken, new_password: newPassword }),
    });
  }

  /**
   * Send a magic link for passwordless login
   */
  async sendMagicLink(email: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }

  private clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }
}

export const authService = new AuthService();